import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Loader2, PenLine } from "lucide-react";
import { Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-white/5 border-white/10 h-12 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50";

const emptyReview = { name: "", event_type: "", rating: 0, text: "" };

export default function ReviewForm({ onSubmitted }) {
  const [form, setForm] = useState(emptyReview);
  const [loading, setLoading] = useState(false);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error("Please tap the stars to rate your experience.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, form);
      setForm(emptyReview);
      toast.success("Thank you! Your review is now live on our website.");
      if (onSubmitted) onSubmitted(data);
    } catch (err) {
      toast.error("Something went wrong posting your review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="review-form"
      onSubmit={submitReview}
      className="glass rounded-3xl p-8 sm:p-10 space-y-6"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full border border-primary/30 flex items-center justify-center text-primary">
          <PenLine size={20} />
        </div>
        <div>
          <Overline>Leave a Review</Overline>
          <h2 className="font-serif text-2xl sm:text-3xl mt-1">
            Celebrated with us? <em className="text-primary">Tell everyone.</em>
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="review-name">Your Name *</Label>
          <Input
            id="review-name"
            data-testid="review-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Full name"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="review-event">Your Event</Label>
          <Input
            id="review-event"
            data-testid="review-event-input"
            value={form.event_type}
            onChange={(e) => setForm({ ...form, event_type: e.target.value })}
            placeholder="e.g. Wedding Reception"
            className={inputCls}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Your Rating *</Label>
        <div className="flex gap-2" data-testid="review-rating-group">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`review-star-${n}`}
              onClick={() => setForm({ ...form, rating: n })}
              aria-label={`Rate ${n} stars`}
              className="transition-transform duration-200 hover:scale-125"
            >
              <Star
                size={28}
                className={
                  n <= form.rating
                    ? "fill-primary text-primary"
                    : "text-muted-foreground/40"
                }
              />
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="review-text">Your Review *</Label>
        <Textarea
          id="review-text"
          data-testid="review-text-input"
          required
          rows={4}
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          placeholder="How was your celebration? The music, the energy, the moments..."
          className="bg-white/5 border-white/10 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50"
        />
      </div>

      <button
        type="submit"
        data-testid="review-submit-button"
        disabled={loading}
        className="w-full rounded-full bg-primary text-primary-foreground py-4 text-sm font-medium tracking-widest uppercase transition-[transform,box-shadow,opacity] duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(255,0,127,0.35)] disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-3"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        {loading ? "Posting..." : "Post My Review"}
      </button>
      <p className="text-xs text-muted-foreground/60 text-center tracking-wider">
        Your review appears on our website instantly.
      </p>
    </form>
  );
}
